/**
 * Run an async task over a list of items with bounded parallelism.
 *
 * `Promise.all(items.map(task))` is fine for ~10 items but breaks at scale:
 * 100+ parallel CF/CRM calls hit upstream concurrency caps (Zoho ~5/user,
 * HubSpot ~10/user, browser HTTP/1.1 ~6/origin) and 10-20% of them come
 * back as empty / 429 / network error. A small concurrency window keeps the
 * burst within whatever the upstream tolerates while still pipelining.
 *
 * Implementation: spawn `limit` "workers" that each pull the next index
 * off a shared cursor and `await` it sequentially. Total workers complete
 * when the cursor passes the end. No external deps.
 *
 * Errors thrown by the worker are swallowed at the call site (caller's
 * try/catch inside `task`) — this helper itself never rejects unless the
 * worker function does. If you need fail-fast, throw from `task`.
 */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<void>,
): Promise<void> {
  if (items.length === 0) return;
  const concurrency = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;

  const worker = async () => {
    while (cursor < items.length) {
      const i = cursor++;
      await task(items[i], i);
    }
  };

  await Promise.all(Array.from({ length: concurrency }, worker));
}
